import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollText, RefreshCw, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_STYLES = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
};
const ACTION_DOT = {
  create: 'bg-emerald-500',
  update: 'bg-blue-500',
  delete: 'bg-red-500',
};

const fmtTime = (ts) => {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};

const renderVal = (v) => {
  if (v === null || v === undefined || v === '') return '∅';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/**
 * Reusable per-record change timeline. Drop into any record page:
 *   <RecordAuditTrail doctype="sales_order" recordId={order.sales_order_number} />
 */
const RecordAuditTrail = ({ doctype, recordId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!doctype || !recordId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/audit/record/${doctype}/${recordId}`);
      setItems(data.items || []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [doctype, recordId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-6" data-testid="record-audit-trail">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-amber-600" />
          Change History
        </h3>
        <Button variant="ghost" size="sm" onClick={load} data-testid="record-audit-refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 py-8 text-center">Loading change history…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center" data-testid="record-audit-empty">
          No recorded changes for this record yet.
        </div>
      ) : (
        <ol className="relative border-l border-gray-200 ml-2 space-y-6">
          {items.map((e) => (
            <li key={e.id} className="ml-5" data-testid={`record-audit-entry-${e.id}`}>
              <span className={`absolute -left-[7px] w-3.5 h-3.5 rounded-full ring-4 ring-white ${ACTION_DOT[e.action] || 'bg-gray-400'}`} />
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={`${ACTION_STYLES[e.action] || 'bg-gray-100 text-gray-700'} border-0`}>{e.action}</Badge>
                <span className="text-sm font-medium text-gray-800">{e.username || 'system'}</span>
                {!e.success && <Badge className="bg-red-100 text-red-700 border-0">failed {e.status_code}</Badge>}
                <span className="text-xs text-gray-400">· {fmtTime(e.timestamp)}</span>
              </div>

              {Array.isArray(e.changes) && e.changes.length > 0 ? (
                <div className="mt-2 border rounded-md divide-y bg-gray-50/60">
                  {e.changes.map((c) => (
                    <div key={c.field} className="grid grid-cols-[1fr,auto,1fr] items-center gap-2 px-3 py-1.5 text-xs">
                      <span className="font-medium text-gray-700">{c.field}</span>
                      <span className="text-red-500 line-through break-all justify-self-end max-w-[160px] truncate" title={renderVal(c.old)}>{renderVal(c.old)}</span>
                      <span className="inline-flex items-center gap-1 text-emerald-700 break-all max-w-[180px] truncate" title={renderVal(c.new)}>
                        <ArrowRight className="w-3 h-3 shrink-0 text-gray-400" />{renderVal(c.new)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-xs text-gray-400">
                  {e.action === 'create' ? 'Record created' : e.action === 'delete' ? 'Record deleted' : 'No field-level changes recorded'}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
};

export default RecordAuditTrail;
